/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { AcceptDenyModal } from 'Components/Modal/AcceptDenyModal'
import { Box, CircularProgress } from '@mui/material'
import { useState, useEffect } from 'react'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { type postError, Role, type ChildEditValues, type DocumentTypeRequirement } from 'core/types'
import { ReferralView } from 'Components/ChildDetails/ReferralView'
import { type ReferralData } from 'Pages/Referral/ReferralDetail'
import { ReferralEditModal } from 'Pages/Referral/ReferralEditModal'
import { EnrollmentEditModal } from 'Pages/ServiceProvider/Enrollments/EnrollmentEditModal'
import { toast } from 'react-toastify'
import { type childServiceSettings } from 'ContextProviders/AddChildProvider'
import { usePermissions } from 'hooks/use-permissions'
import { useAuth } from 'hooks/use-auth'

export interface EnrollmentDetailProps {
  hideIncomeReapply?: boolean
  enrollmentID: number
  childID: number
  open: boolean
  onClose: () => void
  onEnroll?: () => void
  onExit?: () => void
  onReapplyIncome?: () => void
  allowWaitlist: boolean
}

export function EnrollmentDetails (props: EnrollmentDetailProps): JSX.Element {
  const [enrollment, setEnrollment] = useState<ReferralData>()
  const [loaded, setLoaded] = useState(false)
  const [docTypes, setDocTypes] = useState<DocumentTypeRequirement[]>([])
  const [refresh, setRefresh] = useState(new Date())
  const [showCreateNewReferral, setShowCreateNewReferral] = useState(false)
  const [existingReferrals, setExistingReferrals] = useState<Array<
  { programID: number
    serviceID: number
    allowConcurrent: boolean
    timeFrameID: number
  }>>([])
  const perms = usePermissions()
  const [showEditModal, setShowEditModal] = useState(false)
  const auth = useAuth()

  useEffect(() => {
    async function getData (): Promise<void> {
      const request = await sendGet(`/Enrollment/GetEnrollment?id=${props.enrollmentID}`, {})
      const referral: ReferralData = request.response
      referral.id = referral.referralID ?? 0
      setEnrollment(referral)

      const { response: rsp, success: suc } = await sendGet(`/Enrollment/GetEnrollmentDocumentTypes?enrollmentId=${props.enrollmentID}`, {})
      if (suc) {
        setDocTypes(rsp as DocumentTypeRequirement[])
      }

      const { response, success } = await sendGet(`/ChildDetails/GetAllServices?id=${props.childID}`, {})
      if (success) {
        setExistingReferrals(response.response)
      }
      setLoaded(true)
    }
    void getData()
  }, [props.enrollmentID, refresh])

  const handleClose = (): void => {
    props.onClose()
  }

  const handleRefresh = (): void => {
    setRefresh(new Date())
    if (props.onReapplyIncome != null) {
      props.onReapplyIncome()
    }
  }

  const printDetails = (): void => {
    window.open(enrollment?.reportURL, '_blank')?.focus()
  }

  const editClosed = (updated: boolean): void => {
    setShowEditModal(false)
    if (updated) {
      setRefresh(new Date())
    }
  }

  const handleSubmitNew = async (settings: childServiceSettings): Promise<boolean> => {
    const newReferral = {
      child: { id: enrollment?.childID } as ChildEditValues,
      serviceSettings: settings,
      districtSettings: { isdid: enrollment?.isdid }
    }

    const { response } = await sendPost('/ReferralEdit/CreateServiceReferral', newReferral)
    const error: postError[] = response.errors
    const success: boolean = response.success
    if (response != null) {
      if (success) {
        toast.success('New referral created')
        setShowCreateNewReferral(false)
        return true
      } else {
        toast.error(error.map(e => e.error).join(','))
      }
    }

    return false
  }
  if (!loaded) return <CircularProgress role='loading' />
  else if (enrollment == null) return <div>Record not found</div>
  else {
    const content = <Box >
      <ReferralView
        hideIncomeReapply={props.hideIncomeReapply}
        onPrint={printDetails}
        referral={enrollment}
        enrollmentId={props.enrollmentID}
        type='enrollment'
        docTypes={docTypes}
        hideEditButtons={!perms.Enrollment_Edit && !(perms.CareNetwork_Enrollment_Edit && auth.user?.securityLevel === Role.ISD_USER)}
        onRefresh={handleRefresh}
        onEdit={() => { setShowEditModal(true) }}
        onCreateNew={setShowCreateNewReferral}
        onEditHouseholdMembers={() => { }}
      />
      {showCreateNewReferral && <ReferralEditModal
        open={true} referral={{ childID: enrollment.childID, isdid: enrollment.isdid } as ReferralData}
        onSubmit={handleSubmitNew}
        onClose={() => { setShowCreateNewReferral(false) }}
        status={'Pending'}
        isNew={true}
        existingReferrals={existingReferrals}
        allowWaitlist={props.allowWaitlist}
      />}
      {showEditModal && <EnrollmentEditModal
        enrollmentID={props.enrollmentID}
        onClose={editClosed}
      />
      }
    </Box>

    return <AcceptDenyModal
      className='Modal Referral'
      open={props.open}
      title={'Student Information'}
      dialogContent={content}
      onClose={handleClose}
      onAccept={props.onEnroll}
      onDeny={props.onExit}
      maxWidth={'sm'}
      denyButtonText='Exit'
      data-testid='enrollment-details-modal'
    />
  }
}
